import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Simulation } from 'src/app/core/models/simulation.model';
import { Clone } from 'src/app/shared/utils/clone';
import { environment } from 'src/environments/environment';
import { RemoveAllIds } from '../../../shared/utils/remove-all-ids/remove-all-ids';
import { RemoveProperty } from '../../../shared/utils/remove-property/remove-property';
import { CONSTANTS } from '../../constants/constants';
import { DEFAULT_FUNCTIONS } from '../../constants/default-functions';
import { GRAPHIC_OBJECT_FIELDS } from '../../enums/graphic-object-fields';
import { ConnectionGraphicProperties } from '../../models/3D/graphic-properties/connection-graphic-properties.model';
import { FloatingGraphicProperties } from '../../models/3D/graphic-properties/floating-graphic-properties.model';
import { GlobalGraphicProperties } from '../../models/3D/graphic-properties/global-graphic-properties';
import { LineGraphicProperties } from '../../models/3D/graphic-properties/line-graphic-properties.model';
import { ReferenceSystemGraphicProperties } from '../../models/3D/graphic-properties/reference-system-graphic-properties.model';
import { SimulationMeta } from '../../models/extra-models/simulation-meta.model';
import { InternalAccessoryConfig } from '../../models/internal-accessory-config.model';
import { Line } from '../../models/line.model';
import { SegmentConfig } from '../../models/segment-config.model';
import { DataSharingService } from '../data-sharing/data-sharing.service';
import { EnvironmentPropsService } from '../environment-props/environment-props.service';
import { IDBService } from '../idb/idb.service';
import { MapsService } from './objects/maps/maps.service';
import { TitleCopyService } from './objects/title-copy/title-copy.service';

@Injectable({
  providedIn: 'root',
})
export class SimulationManagerService {
  simulation: Simulation = new Simulation();
  simulationSource = new BehaviorSubject<Simulation>(new Simulation());
  maxId: number = 0;

  constructor(
    private idbService: IDBService,
    private mapsService: MapsService,
    private environmentPropsService: EnvironmentPropsService,
    private titleCopyService: TitleCopyService,
    private dataSharingService: DataSharingService,
  ) {
    this.startManager();
  }

  async startManager(): Promise<void> {
    this.mapsService.resetMap();

    const resp = await this.idbService.getById(CONSTANTS.STORAGE.SIMULATION);
    const existentSimulation = resp?.data;

    if (existentSimulation && Object.keys(existentSimulation).length) {
      this.simulation = existentSimulation as Simulation;
      this.populateGraphicProperties(this.simulation);
      this.maxId = this.getMaxId(this.simulation, 0);
      this.changeSimulation();
      return;
    }

    this.startEmpty();
  }

  async hasSimulation(): Promise<boolean> {
    const dbResponse = await this.idbService.getById(CONSTANTS.STORAGE.SIMULATION);
    return !!dbResponse.data;
  }

  startEmpty(): void {
    this.simulation = new Simulation();
    this.maxId = this.getMaxId(this.simulation, 0);
    this.initEmptySimulationListsForTempSimulation(this.simulation);
  }

  delete(): void {
    delete this.simulation;
    this.changeSimulation();
  }

  setSimulation(simulation: Simulation, triggerChanges?: boolean): void {
    this.updatePreviousGraphicRefs();
    this.populateGraphicProperties(simulation);
    this.simulation = simulation;
    this.adjustLines();
    this.setDefaultFunctions();
    if (triggerChanges) {
      this.changeSimulation();
    }
    this.mapsService.resetMap();
    this.maxId = this.getMaxId(this.simulation, 0);
  }

  initEmptySimulationListsForTempSimulation(simulation): void {
    simulation.global = [];
    simulation.soil = [];
    simulation.rotationalFunction = [];
    simulation.matFlexLine = [];
    simulation.matConnector = [];
    simulation.matRigidTube = [];
    simulation.matStressJoint = [];
    simulation.matCable = [];
    simulation.matStiffener = [];
    simulation.matFlexJoint = [];
    simulation.floating = [];
    simulation.connection = [];
    simulation.line = [];
    simulation.strake = [];
    simulation.floater = [];
    simulation.stiffener = [];
    this.simulation.referenceSystem = [];

    this.addAnflexFields(simulation);
  }

  addAnflexFields(simulation): void {
    if (!environment.anflexWeb) {
      return;
    }
    simulation.fluidFunction = [];
  }

  setDefaultFunctions(): void {
    if (!environment.anflexWeb || this.simulation.timeFunction?.length) {
      return;
    }
    this.simulation.timeFunction = this.getDefaultFunctionsWithIds(DEFAULT_FUNCTIONS.TIME);
    this.simulation.displacementFunction = this.getDefaultFunctionsWithIds(
      DEFAULT_FUNCTIONS.DISPLACEMENT,
    );
    this.simulation.rotationalFunction = this.getDefaultFunctionsWithIds(
      DEFAULT_FUNCTIONS.ROTATIONAL,
    );
    this.simulation.temperatureFunction = this.getDefaultFunctionsWithIds(
      DEFAULT_FUNCTIONS.TEMPERATURE,
    );
  }

  getDefaultFunctionsWithIds(anflexFunction: any[]): any {
    return anflexFunction.map((func) => {
      return Object.assign(func, { internalId: this.getNewId() });
    });
  }

  startNewSimulation(simulationMeta: SimulationMeta): void {
    this.simulation = new Simulation();
    this.simulation.title = simulationMeta.title;
    this.simulation.floatingProductionUnit = simulationMeta.floatingProductionUnit;
    this.simulation.supportNumber = simulationMeta.supportNumber;
    this.simulation.interlId = simulationMeta.interlId;
    this.simulation.wellInterconnection = simulationMeta.wellInterconnection;
    this.simulation.riserFunction = simulationMeta.riserFunction;
    this.simulation.projectControlId = simulationMeta.projectControlId;
    this.simulation.revision = simulationMeta.revision;
    this.simulation.projectPhase = simulationMeta.projectPhase;
    this.simulation.complementaryObservation = simulationMeta.complementaryObservation;
    this.mapsService.resetMap();
    this.initEmptySimulationListsForTempSimulation(this.simulation);
  }

  createSimulation(simulation: Simulation, simulationMeta: SimulationMeta): Simulation {
    RemoveAllIds(simulation);
    if (simulation?.global && !(simulation.global instanceof Array)) {
      simulation.global = [simulation.global];
    }
    this.simulation = simulation;
    this.simulation.title = simulationMeta.title;
    this.simulation.floatingProductionUnit = simulationMeta.floatingProductionUnit;
    this.simulation.riserFunction = simulationMeta.riserFunction;
    this.simulation.interlId = simulationMeta.interlId;
    this.simulation.wellInterconnection = simulationMeta.wellInterconnection;
    this.simulation.supportNumber = simulationMeta.supportNumber;
    this.simulation.projectControlId = simulationMeta.projectControlId;
    this.simulation.revision = simulationMeta.revision;
    this.simulation.projectPhase = simulationMeta.projectPhase;
    this.simulation.complementaryObservation = simulationMeta.complementaryObservation;
    this.mapsService.resetMap();
    this.maxId = this.getMaxId(this.simulation, 0);

    return this.simulation;
  }

  changeSimulation(triggerChanges = true): void {
    if (triggerChanges) {
      this.simulationSource.next(this.simulation);
    }
    const parsedSimulation = this.getSimulationForStorage();
    this.idbService.update(CONSTANTS.STORAGE.SIMULATION, parsedSimulation);
  }

  getSimulationForStorage(): Simulation {
    const simulationCopy = Clone(this.simulation);
    Object.entries(GRAPHIC_OBJECT_FIELDS).forEach(([_, field]) => {
      if (simulationCopy.hasOwnProperty(field)) {
        simulationCopy[field].forEach((object) => {
          object.graphicProperties = null;
          object.needsUpdate = undefined;
        });
      }
    });
    return simulationCopy;
  }

  getSimulation(): Simulation {
    return this.simulationSource.getValue();
  }

  getNewId(): number {
    this.maxId++;
    return this.maxId;
  }

  private getMaxId(simulation, currentMaxId): number {
    let idTest = 0;

    Object.keys(simulation).forEach((key) => {
      if (key === 'internalId') {
        currentMaxId = simulation[key] > currentMaxId ? simulation[key] : currentMaxId;
      }

      if (simulation[key] instanceof Array) {
        simulation[key].forEach((element) => {
          if (typeof element === 'object' && element !== null) {
            idTest = this.getMaxId(element, currentMaxId);
            currentMaxId = idTest > currentMaxId ? idTest : currentMaxId;

            this.saveInMaps(key, element);
          }
        });
      }
    });

    return currentMaxId;
  }

  saveInMaps(key: string, object: any): void {
    if (!object.internalId) {
      return;
    }
    this.mapsService.saveInAllMaps(key, object);
  }

  hasAnyMaterial(): boolean {
    return !!(
      this.simulation?.matFlexLine?.length ||
      this.simulation?.matFlexJoint?.length ||
      this.simulation?.matRigidTube?.length ||
      this.simulation?.matStressJoint?.length ||
      this.simulation?.matConnector?.length ||
      this.simulation?.matStiffener?.length
    );
  }

  adjustLines(): void {
    if (!this.simulation?.line || !this.simulation?.line?.length) {
      return;
    }
    this.simulation.line = this.simulation.line.map((line) => this.turnLineConsistent(line));
  }

  private createEmptySegConfig(numSegments: number): SegmentConfig[] {
    const segConf: SegmentConfig[] = [];
    for (let i = 0; i < numSegments; i++) {
      const nConf: SegmentConfig = new SegmentConfig();
      nConf.flexjointBeginId = -1;
      nConf.stiffenerBeginId = -1;
      nConf.flexjointEndId = -1;
      nConf.stiffenerEndId = -1;
      nConf.orientation = CONSTANTS.SEGMENT_ORIENTATION_TYPES.NONE;
      nConf.floaterConfig = [];
      nConf.strakeConfig = [];
      segConf.push(nConf);
    }
    return segConf;
  }

  private turnLineConsistent(line: Line): Line {
    const numMaterials: number = line.segmentMaterialId.length;
    if (line?.segConfig == null || line?.segConfig?.length < numMaterials) {
      line.segConfig = this.createEmptySegConfig(numMaterials);
    }
    return line;
  }

  updatePreviousGraphicRefs(): void {
    if (!this.isAnflex) {
      return;
    }

    Object.entries(GRAPHIC_OBJECT_FIELDS).forEach(([_, field]) => {
      if (this.simulation.hasOwnProperty(field)) {
        this.simulation[field].forEach((object) => {
          if (object.graphicProperties) {
            this.mapsService.setGraphicPropById(object.internalId, object.graphicProperties);
          }
        });
      }
    });
  }

  populateGraphicProperties(simulation: Simulation): void {
    if (!this.isAnflex) {
      return;
    }

    Object.entries(GRAPHIC_OBJECT_FIELDS).forEach(([_, field]) => {
      if (simulation.hasOwnProperty(field)) {
        if (field === GRAPHIC_OBJECT_FIELDS.GLOBAL) {
          this.saveGlobalGraphicProp(simulation, field);
        } else {
          simulation[field].forEach((object) => {
            this.saveGraphicProp(field, object);
          });
        }
      }
    });
  }

  saveGlobalGraphicProp(simulation: Simulation, field: GRAPHIC_OBJECT_FIELDS): void {
    if (simulation[field][0]) {
      this.saveGraphicProp(field, simulation[field][0]);
      return;
    }
    this.saveGraphicProp(field, simulation[field]);
  }

  saveGraphicProp(field: GRAPHIC_OBJECT_FIELDS, object: any): void {
    const savedGraphicProp = this.mapsService.getGraphicPropById(object.internalId);
    object.graphicProperties = savedGraphicProp || this.getPropertyByField(field);

    if (object.needsUpdate === undefined) {
      object.needsUpdate = true;
    }
  }

  getPropertyByField(field): any {
    switch (field) {
      case GRAPHIC_OBJECT_FIELDS.LINE:
        return new LineGraphicProperties();

      case GRAPHIC_OBJECT_FIELDS.CONNECTION:
        return new ConnectionGraphicProperties();

      case GRAPHIC_OBJECT_FIELDS.FLOATING:
        return new FloatingGraphicProperties();

      case GRAPHIC_OBJECT_FIELDS.GLOBAL:
        return new GlobalGraphicProperties();

      case GRAPHIC_OBJECT_FIELDS.REFERENCE_SYSTEM:
        return new ReferenceSystemGraphicProperties();
    }
  }

  appendObjects(
    type: string,
    destinationList: any[],
    sourceList: any[],
    oldAdjustmentMap: any,
  ): void {
    const adjustmentMap: any = {};
    if (!sourceList || sourceList?.length <= 0) {
      return;
    }
    sourceList.forEach((elem) => {
      const newId = this.getNewId();
      adjustmentMap[elem.internalId] = newId;
      elem.internalId = newId;
      elem.title = this.titleCopyService.getPossibleName(elem.title);
      destinationList.push(elem);
      this.mapsService.saveInAllMaps(type, elem);
    });

    Object.assign(oldAdjustmentMap, adjustmentMap);
  }

  replaceId(id: number, adjustmentMap: any): number {
    if (adjustmentMap.hasOwnProperty(id)) {
      return adjustmentMap[id];
    }
    return id;
  }

  adjustInternalAccConfig(
    accConfig: InternalAccessoryConfig,
    adjustmentMap,
  ): InternalAccessoryConfig {
    accConfig.accId = this.replaceId(accConfig.accId, adjustmentMap);
    return accConfig;
  }

  adjustSegmentConfig(segmentConfig: SegmentConfig, adjustmentMap: any): SegmentConfig {
    segmentConfig.flexjointBeginId = this.replaceId(segmentConfig.flexjointBeginId, adjustmentMap);
    segmentConfig.flexjointEndId = this.replaceId(segmentConfig.flexjointEndId, adjustmentMap);
    segmentConfig.stiffenerBeginId = this.replaceId(segmentConfig.stiffenerBeginId, adjustmentMap);
    segmentConfig.stiffenerEndId = this.replaceId(segmentConfig.stiffenerEndId, adjustmentMap);
    segmentConfig.floaterConfig = segmentConfig.floaterConfig.map((x) =>
      this.adjustInternalAccConfig(x, adjustmentMap),
    );
    segmentConfig.strakeConfig = segmentConfig.strakeConfig.map((x) =>
      this.adjustInternalAccConfig(x, adjustmentMap),
    );

    return segmentConfig;
  }

  adjustPreExistentLine(line: Line, adjustmentMap: any): Line {
    line.internalId = this.getNewId();
    line.title = this.titleCopyService.getPossibleName(line.title);
    line.segmentSoilId = line.segmentSoilId.map((segSoils) =>
      this.replaceId(segSoils, adjustmentMap),
    );
    line.segmentMaterialId = line.segmentMaterialId.map((segMaterials) =>
      this.replaceId(segMaterials, adjustmentMap),
    );
    line.segConfig = line.segConfig.map((segConfig) =>
      this.adjustSegmentConfig(segConfig, adjustmentMap),
    );
    delete line.fluidDatasheet;
    line.needsUpdate = true;
    return line;
  }

  mergeSimulations(simulations: Simulation[]): void {
    simulations.forEach((simulation) => {
      const adjustmentMap = {};
      RemoveAllIds(simulation);
      this.appendObjects(CONSTANTS.SOIL, this.simulation.soil, simulation.soil, adjustmentMap);
      this.appendObjects(
        CONSTANTS.MATERIAL_TYPES.FLEX_LINE,
        this.simulation.matFlexLine,
        simulation.matFlexLine,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.MATERIAL_TYPES.CABLE,
        this.simulation.matCable,
        simulation.matCable,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.MATERIAL_TYPES.CONNECTOR,
        this.simulation.matConnector,
        simulation.matConnector,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.MATERIAL_TYPES.RIGID_TUBE,
        this.simulation.matRigidTube,
        simulation.matRigidTube,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.MATERIAL_TYPES.STRESS_JOINT,
        this.simulation.matStressJoint,
        simulation.matStressJoint,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.ACCESSORY_TYPES.FLOATER,
        this.simulation.floater,
        simulation.floater,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.ACCESSORY_TYPES.STIFFENER,
        this.simulation.stiffener,
        simulation.stiffener,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.ACCESSORY_TYPES.STRAKE,
        this.simulation.strake,
        simulation.strake,
        adjustmentMap,
      );
      this.appendObjects(
        CONSTANTS.ACCESSORY_TYPES.FLEXJOINT,
        this.simulation.matFlexJoint,
        simulation.matFlexJoint,
        adjustmentMap,
      );

      simulation.line.forEach((line) => {
        const adjustedLine = this.adjustPreExistentLine(line, adjustmentMap);
        this.simulation.line.push(adjustedLine);
        this.mapsService.saveInAllMaps(CONSTANTS.LINE, adjustedLine);
        this.dataSharingService.importedLine$.next(adjustedLine);
      });

      this.mapsService.resetMap();
      this.maxId = this.getMaxId(this.simulation, 0);

      this.populateGraphicProperties(simulation);
      this.changeSimulation();
    });
  }

  get simulationWithoutGraphicProps(): Simulation {
    const simulationCopy = Clone(this.simulation);
    RemoveProperty(simulationCopy, new Set([CONSTANTS.GRAPHIC_PROPERTIES]));
    return simulationCopy;
  }

  get isAnflex(): boolean {
    return this.environmentPropsService.isAnflex;
  }
}
